import { useCallback, useContext, useEffect, useRef } from 'react'
import { RoomContext, RoomContextType, SfuRemoteAudio, SfuRemoteMedia } from '../RoomContext'
import {
    RemoteAudioTrack,
    RemoteParticipant,
    RemoteTrack,
    RemoteTrackPublication,
    Room,
    Track,
    TrackPublication,
    VideoQuality,
} from 'livekit-client'
import { logger } from './Logger'
import { UserContext, UserContextType } from '../UserContext'
import useRoomContextRef from './useRoomContextRef'
import { MusePeer, PeerManifest } from './RoomContextMusePeer'
import useUserContextRef from './useUserContextRef'
import { env } from './EnvironmentVariables'
import * as Constants from './Constants'
import { videoSubscriptionManager } from './VideoSubscriptionManager'
import { speakerDetectionManager } from './SpeakerDetectionManager'
import useRoomActions from './useRoomActions'

const log = logger.createChildLogger({ name: 'SfuIncomingMediaStateSynchronizer' })

type UpdatePeerRoomDispatch = {
    sessionId: string
    peer: Partial<MusePeer>
}

type TrackUnsubscribedData = [UpdatePeerRoomDispatch, { deviceId: string }]

/**
/**
 * In order for an audio media to be fully received, these 2 conditions have to be met:
 *   1. Peer must exist in the room context and an entry for the device (keyed by the deviceId) must
 *      exist in the manifest. (`RoomState.peers[sessionId].manifest.remoteSfuAudios`)
 *   2. The publication must be present in the LiveKit room.
 *
 * Possible events:
 *  - a. Peer object gets created in RoomContext
 *  - b. manifest arrives / updated
 *  - c. LiveKit publication is subscribed
 *
 * Possible combinations of the order of events and how the transaction is handled:
 *  - a b c : onTrackSubscribed() will have the data necessary to complete the transaction.
 *  - a c b : onManifestUpdated() will have the data necessary to complete the transaction.
 *  - b a c : (b) sets the latest manifest ref; (a) creates peer object along with the stored manifest; then,
 *            onManifestUpdated() will have the data necessary to complete the transaction.
 *  - b c a : (b) sets the latest manifest ref; (c) sets LiveKit's internal state; then
 *            onPeerAdded() will have the data necessary to complete the transaction.
 *  - c a b : onManifestUpdated() will have the data necessary to complete the transaction.
 *  - c b a : same as (b a c)
 *
 * incomingMediaStateSynchronizer
 * - peer added
 * - peer removed
 * - track added
 * - track removed
 * - manifest updated
 */
export default function useSfuIncomingMediaStateSynchronizer() {
    const { state } = useContext(UserContext) as UserContextType
    const { state: roomState, dispatch: roomDispatch } = useContext(RoomContext) as RoomContextType

    const { roomStateRef } = useRoomContextRef()
    const { userStateRef } = useUserContextRef()

    const { foreachRemoteAudioDevices } = useRoomActions()

    // During warmup mode, the `onManifestUpdated()` has a higher debouncing time so that
    // the updates are better collated and dispatched at once for improved performance.
    const isWarmupModeRef = useRef(true)

    const _syncContextPub = useCallback(
        (refPeer: MusePeer, manifest: PeerManifest, pub: RemoteTrackPublication, delta: UpdatePeerRoomDispatch) => {
            const sessionId = refPeer.sessionId

            if (!pub.track) {
                log.verbose('syncContext', 'pub.track does not exist!', { sessionId })
                return delta
            }
            if (!pub.trackInfo) {
                log.verbose('syncContext', 'pub.trackInfo does not exist!', { sessionId })
                return delta
            }

            if (pub.source === Track.Source.Camera) {
                if (env.TEST_NO_SUBSCRIBE_VIDEO_MEDIA) {
                    return delta
                }

                const track = toSfuRemoteMedia(pub.track, pub.trackInfo.name)
                if (refPeer.remoteSfuVideo?.mediaStreamID !== track.mediaStreamID) {
                    delta.peer.remoteSfuVideo = track
                }
            } else if (pub.source === Track.Source.ScreenShare) {
                const track = toSfuRemoteMedia(pub.track, pub.trackInfo.name)
                if (refPeer.remoteSfuDesktop?.mediaStreamID !== track.mediaStreamID) {
                    delta.peer.remoteSfuDesktop = track
                }
            } else if (pub.kind === Track.Kind.Audio) {
                if (env.TEST_NO_SUBSCRIBE_AUDIO_MEDIA) {
                    return delta
                }

                const media = toSfuRemoteMedia(pub.track, pub.trackInfo.name)
                const { deviceId } = media

                // does the manifest for this device exist? Then update context, otherwise let
                // the manfiest handler do it.
                const m = (manifest.localSfuAudios || []).find((a) => a.info.deviceId === deviceId)
                if (m) {
                    const audioMedia = media as SfuRemoteAudio
                    audioMedia.info = m.info

                    if (!refPeer.remoteSfuAudios.find((a) => a.info.deviceId === audioMedia.info.deviceId)) {
                        if (!delta.peer.remoteSfuAudios) {
                            delta.peer.remoteSfuAudios = [...refPeer.remoteSfuAudios]
                        }
                        delta.peer.remoteSfuAudios.push(audioMedia)
                    } else {
                        log.verbose('syncContext', 'device already exists in remoteSfuAudios; skipping.', {
                            sessionId,
                            deviceId,
                        })
                    }
                } else {
                    log.verbose(
                        'syncContext',
                        'Could not find the localSfuAudios entry for ' +
                            `deviceId="${deviceId}" in the remote peer manifest. Skipping.`,
                        { sessionId, deviceId }
                    )
                }
            }

            return delta
        },
        []
    )

    const _syncContext = useCallback((participant: RemoteParticipant, manifest: PeerManifest) => {
        const sessionId = participant.identity

        log.isSilly() && log.silly('syncContext', 'start processing', { sessionId, manifest })

        const refPeer = roomStateRef.current.peers[sessionId]
        if (!refPeer) {
            log.verbose('syncContext', 'peer not found in RoomContext.peers.', { sessionId })
            return
        }

        let delta: UpdatePeerRoomDispatch = { sessionId, peer: {} }

        participant.trackPublications.forEach((pub_: TrackPublication) => {
            const pub = pub_ as RemoteTrackPublication
            log.isSilly() && log.silly('syncContext', `calling syncContextPub for ${pub.trackSid}.`, { sessionId })
            delta = _syncContextPub(refPeer, manifest, pub, delta)
        })

        if (Object.keys(delta.peer).length > 0) {
            log.isVerbose() &&
                log.verbose('syncContext', `return roomDispatch delta.`, {
                    sessionId,
                    remoteSfuVideoChanged: !!delta.peer.remoteSfuVideo,
                    remoteSfuDesktopChanged: !!delta.peer.remoteSfuDesktop,
                    remoteSfuAudios: delta.peer.remoteSfuAudios
                        ? delta.peer.remoteSfuAudios.map((a) => ({
                              deviceId: a.deviceId,
                              label: a.label,
                              mediaStreamID: a.mediaStreamID,
                          }))
                        : null,
                })

            if (delta.peer.remoteSfuAudios) {
                log.info('syncContext', `roomDispatch delta setting remoteSfuAudios.`, {
                    remoteSfuAudios: delta.peer.remoteSfuAudios.map((a) => ({
                        deviceId: a.deviceId,
                        label: a.label,
                        mediaStreamID: a.mediaStreamID,
                    })),
                })
            }

            return delta
        } else {
            log.verbose('syncContext', 'no changes; no-op.')
            return null
        }
    }, [])

    const _applyVideoSubscriptionLimit = useCallback(() => {
        const sfuRoom = roomStateRef.current.sfuRoom
        if (!sfuRoom) {
            return
        }

        videoSubscriptionManager.applyVideoSubscriptionLimit()
    }, [])

    /**
     * Should be called when a peer gets added to the RoomContext.peers.
     */
    const onPeerAdded = useCallback((room: Room, peers: MusePeer[]) => {
        log.isVerbose() && log.verbose('onPeerAdded', 'called.', { sessionIds: peers.map((a) => a.sessionId) })

        const deltas: UpdatePeerRoomDispatch[] = []

        for (let peer of peers) {
            let participant: RemoteParticipant | null = null
            for (let [_, p] of room.remoteParticipants) {
                if (p.identity === peer.sessionId) {
                    participant = p
                }
            }

            if (participant) {
                log.verbose('onPeerAdded', `calling syncContext for sessionId="${peer.sessionId}".`)
                const delta = _syncContext(participant, peer.manifest)
                if (delta) {
                    deltas.push(delta)
                }
            } else {
                log.verbose(
                    'onPeerAdded',
                    `peer with sessionId="${peer.sessionId}" not found in sfuRoom participants list.`
                )
            }
        }

        if (deltas.length > 0) {
            log.isVerbose() && log.verbose('onPeerAdded', 'roomDispatch update_peer.', { deltas })
            roomDispatch({
                type: 'update_peer',
                value: deltas,
            })
        }

        if (isWarmupModeRef.current === true) {
            log.isVerbose() && log.verbose('onPeerAdded', 'setting warmup mode to `false`.')
            isWarmupModeRef.current = false
        }

        setTimeout(() => {
            const nPeers = Object.keys(roomStateRef.current.peers).length
            if (nPeers >= Constants.data.max_audio_subscription_users) {
                speakerDetectionManager.startDetection()
            }
        }, 10)
    }, [])

    const onPeerRemoved = useCallback((sessionIds: string[]) => {
        log.verbose('onPeerRemoved', 'called.', { sessionIds })
    }, [])

    const onTrackSubscribedHelper = useCallback((pub: RemoteTrackPublication, participant: RemoteParticipant) => {
        if (env.TEST_DROPPED_MEDIA_RECEPTION_ON_CONNECT) {
            return
        }

        const peerSessionId = participant.identity
        if (peerSessionId === userStateRef.current.sessionId) {
            log.verbose('TrackSubscribed', `skipping because participant is me (sessionId=="${peerSessionId}")`)
            return
        }

        log.verbose('TrackSubscribed', 'received event "TrackSubscribed".', {
            trackSid: pub.trackSid,
            trackName: pub.trackName,
            pubSource: pub.source,
            pubTrackExists: !!pub.track,
            pubTrackInfo: pub.trackInfo,
            peerSessionId: peerSessionId,
            roomStatePeers: Object.keys(roomStateRef.current.peers),
        })

        if (!pub.track || !pub.trackInfo) {
            log.verbose('TrackSubscribed', `skipping because !pub.track || !pub.trackInfo.`)
            return
        }

        const peer = roomStateRef.current.peers[peerSessionId]
        if (!peer) {
            log.verbose(
                'TrackSubscribed',
                `skipping because sessionId="${peerSessionId}" does not exist ` + `in the room peers list.`
            )
            return
        }

        const delta = _syncContext(participant, peer.manifest)
        return delta
    }, [])

    const trackSubscribedDebounceTimerRef = useRef<NodeJS.Timeout | undefined>(undefined)
    const trackSubscribedListRef = useRef<{ pub: RemoteTrackPublication; participant: RemoteParticipant }[]>([])

    const onTrackSubscribed = useCallback((pub: RemoteTrackPublication, participant: RemoteParticipant) => {
        clearTimeout(trackSubscribedDebounceTimerRef.current)
        trackSubscribedListRef.current.push({ pub, participant })

        // if there is a soloed device, then initially add the device as muted. Future state
        // changes will be handled by the RoomController.
        let hasSolo = false
        foreachRemoteAudioDevices(roomStateRef.current.peers, (peer, _, deviceId) => {
            const states = peer.remoteAudioContolStates[deviceId] || {}
            if (states.isSoloed) {
                hasSolo = true
            }
        })
        if (hasSolo && pub.audioTrack && pub.audioTrack instanceof RemoteAudioTrack) {
            try {
                pub.audioTrack.mediaStreamTrack.enabled = false
            } catch (e) {}
        }

        trackSubscribedDebounceTimerRef.current = setTimeout(() => {
            const deltas: UpdatePeerRoomDispatch[] = []
            for (let { pub, participant } of trackSubscribedListRef.current) {
                const delta = onTrackSubscribedHelper(pub, participant)
                if (delta) {
                    deltas.push(delta)
                }
            }
            trackSubscribedListRef.current = []

            if (deltas.length > 0) {
                log.isVerbose() && log.verbose('onTrackSubscribed', 'roomDispatch update_peer.', { deltas })

                roomDispatch({
                    type: 'update_peer',
                    value: deltas,
                })
            }

            _applyVideoSubscriptionLimit()
        }, 500)
    }, [])

    const onTrackUnsubscribed = useCallback((pub: RemoteTrackPublication, participant: RemoteParticipant) => {
        const peerSessionId = participant.identity
        log.info('TrackUnsubscribed', 'received event "TrackUnsubscribed".', {
            trackSid: pub.trackSid,
            trackName: pub.trackName,
            sessionId: peerSessionId,
        })

        if (peerSessionId === state.sessionId) {
            log.verbose('TrackUnsubscribed', `skipping because participant is me (userUrn=="${peerSessionId}")`)
            return
        }

        if (!pub.track || !pub.trackInfo) {
            log.verbose('TrackUnsubscribed', `skipping because !pub.track || !pub.trackInfo.`)
            return
        }

        const peer = roomStateRef.current.peers[peerSessionId]
        if (peer == null) {
            log.verbose(
                'TrackUnsubscribed',
                `skipping because sessionId="${peerSessionId}" does not exist in the room peers list.`
            )
            return
        }

        if (pub.source === Track.Source.Camera) {
            const currentMediaStreamId = roomStateRef.current.peers[participant.identity]?.remoteSfuVideo?.mediaStreamID
            const removedMediaStreamId = pub.track?.mediaStreamID
            if (currentMediaStreamId === removedMediaStreamId) {
                roomDispatch({
                    type: 'update_peer',
                    value: {
                        sessionId: peerSessionId,
                        peer: {
                            remoteSfuVideo: null,
                            remoteVideo: null,
                        },
                    },
                })
            }
        } else if (pub.source === Track.Source.ScreenShare) {
            const currentMediaStreamId =
                roomStateRef.current.peers[participant.identity]?.remoteSfuDesktop?.mediaStreamID
            const removedMediaStreamId = pub.track?.mediaStreamID
            if (currentMediaStreamId === removedMediaStreamId) {
                roomDispatch({
                    type: 'update_peer',
                    value: {
                        sessionId: peerSessionId,
                        peer: {
                            remoteSfuDesktop: null,
                            remoteDesktop: null,
                        },
                    },
                })
            }
        }
        if (pub.kind === Track.Kind.Audio) {
            const remoteSfuAudiosNext = peer.remoteSfuAudios.filter((a) => a.sid !== pub.trackSid)
            const remoteAudiosNext = peer.remoteAudios.filter((a) => {
                // @ts-ignore
                return a?.sid !== pub.trackSid
            })

            log.info('TrackUnsubscribed', 'updating remoteAudios and remoteSfuAudios.', {
                removedTrackSid: pub.trackSid,
                remoteAudiosNext: remoteAudiosNext.map((a) => ({ label: a.info.label, deviceId: a.info.deviceId })),
                remoteSfuAudiosNext: remoteSfuAudiosNext.map((a) => ({
                    label: a.info.label,
                    deviceId: a.info.deviceId,
                    mediaStreamID: a.mediaStreamID,
                })),
            })

            roomDispatch({
                type: 'update_peer',
                value: {
                    sessionId: peerSessionId,
                    peer: {
                        remoteSfuAudios: remoteSfuAudiosNext,
                        remoteAudios: remoteAudiosNext,
                    },
                },
            })
        }
    }, [])

    const manifestUpdateHelper = useCallback((sessionId: string, manifest: PeerManifest) => {
        const sfuRoom = roomStateRef.current.sfuRoom
        if (!sfuRoom) {
            log.error('onManifestUpdated', 'sfuRoom is null!')
            return
        }

        let participant: RemoteParticipant | null = null
        sfuRoom.remoteParticipants.forEach((p) => {
            if (p.identity === sessionId) {
                participant = p
            }
        })

        if (!participant) {
            log.error('onManifestUpdated', 'participant not found in sfuRoom.', { sessionId })
            return
        }

        log.isVerbose() && log.verbose('onManifestUpdated', 'participant found; calling syncContext().', { sessionId })
        const delta = _syncContext(participant, manifest)
        return delta
    }, [])

    const manifestUpdateDebounceTimerRef = useRef<NodeJS.Timeout | undefined>(undefined)
    const manifestUpdateListRef = useRef<{ sessionId: string; manifest: PeerManifest }[]>([])
    const onManifestUpdated = useCallback((sessionId: string, manifest: PeerManifest) => {
        const debounceMs = isWarmupModeRef.current ? 1000 : 10

        log.isVerbose() &&
            log.verbose('onManifestUpdated', 'start.', {
                sessionId,
                manifest,
                isWarmupMode: isWarmupModeRef.current,
                debounceMs,
            })

        clearTimeout(manifestUpdateDebounceTimerRef.current)
        manifestUpdateListRef.current.push({ sessionId, manifest })

        manifestUpdateDebounceTimerRef.current = setTimeout(() => {
            const deltas: UpdatePeerRoomDispatch[] = []
            for (let { sessionId, manifest } of manifestUpdateListRef.current) {
                const delta = manifestUpdateHelper(sessionId, manifest)
                if (delta) {
                    deltas.push(delta)
                }
            }
            manifestUpdateListRef.current = []

            if (deltas.length > 0) {
                log.isVerbose() && log.verbose('onManifestUpdated', 'roomDispatch update_peer.', { deltas })
                roomDispatch({
                    type: 'update_peer',
                    value: deltas,
                })
            }
        }, debounceMs)
    }, [])

    useEffect(
        function resetWarmupModeOnNewSfuRoom() {
            // reset warmup mode when a new room is created.
            if (!isWarmupModeRef.current) {
                log.isVerbose() && log.verbose('useEffect', 'resetting warmup mode to `true`.')
                isWarmupModeRef.current = true
            }
        },
        [roomState.sfuRoom]
    )

    return {
        onPeerAdded,
        onPeerRemoved,
        onTrackSubscribed,
        onTrackUnsubscribed,
        onManifestUpdated,
    }
}

function parseEncodedTrackName(encodedTrackName: string): { label: string; deviceId: string } {
    try {
        log.isSilly() && log.silly(`parseTrackNameToSfuRemoteMedia`, `encodedTrackName="${encodedTrackName}"`)
        const metadata: { label: string; deviceId: string } = JSON.parse(encodedTrackName)
        return metadata
    } catch (e) {
        log.assert(
            `parseTrackNameToSfuRemoteMedia`,
            `failed to parse encodedTrackName="${encodedTrackName}"! ` + `This should never be possible.`
        )

        return { label: '?', deviceId: encodedTrackName }
    }
}

function toSfuRemoteMedia(remoteTrack: RemoteTrack, trackName: string): SfuRemoteMedia {
    try {
        const media = remoteTrack as unknown as SfuRemoteMedia
        const { label, deviceId } = parseEncodedTrackName(trackName)

        // @ts-ignore
        media.fromTopology = 'sfu'
        media.deviceId = deviceId || ''
        media.label = label || ''
        media.track = remoteTrack.mediaStreamTrack
        media.stream = remoteTrack.mediaStream || new MediaStream([remoteTrack.mediaStreamTrack])

        return media
    } catch (e) {
        log.assert(
            `parseTrackNameToSfuRemoteMedia`,
            `failed to parse encodedTrackName="${trackName}"! ` + `This should never be possible.`
        )

        const media = remoteTrack as unknown as SfuRemoteMedia

        // @ts-ignore
        media.fromTopology = 'sfu'
        media.deviceId = trackName || ''
        media.label = ''
        media.track = remoteTrack.mediaStreamTrack
        media.stream = remoteTrack.mediaStream || new MediaStream([remoteTrack.mediaStreamTrack])

        return media
    }
}
